import {
  getLocalTime,
  getUTCDateFromTime,
  isAfterDay,
} from '@gripp/shared-logic';
import { useCallback, useLayoutEffect, useState } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { View } from 'react-native';
import { Text } from 'react-native-paper';

import { useFormContext } from '../formContext';
import { datePickerStyle } from './datePickerStyle';
import { DateRangeSelectorProps } from './dateRangeSelector';

type WebDateTimePickerProps = DateRangeSelectorProps & {
  name: string;
  isStartDate?: boolean;
  label?: string;
};

export const WebDateTimePicker = ({
  isStartDate = false,
  name,
  ...props
}: WebDateTimePickerProps) => {
  const {
    field: { value },
  } = useController({ name, control: props.control });
  const rangeItem = useWatch({
    control: props.control,
    name,
  });
  const [valueInput, setValueInput] = useState<string>(
    value ? getLocalTime(value) : ''
  );
  const { getValues } = useFormContext();

  const updateTimeValue = useCallback(
    (fieldName: string, value: string) => {
      props.setValue(fieldName, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [props]
  );

  const handleChangeTime = useCallback(
    (time?: string) => {
      if (!time) return;
      const timeUTC = getUTCDateFromTime(time);
      if (isStartDate) {
        updateTimeValue(name, timeUTC);
        setValueInput(time);
        const endTime = getValues(name.replace('startTime', 'endTime'));
        if (!endTime || isAfterDay(timeUTC, endTime)) {
          updateTimeValue(name.replace('startTime', 'endTime'), timeUTC);
        }

        return;
      }
      if (name.includes('endTime')) {
        const startTime = getValues(name.replace('endTime', 'startTime'));
        if (startTime && isAfterDay(startTime, timeUTC)) {
          return;
        }
      }
      updateTimeValue(name, timeUTC);
      setValueInput(time);
    },
    [isStartDate, name, updateTimeValue, getValues]
  );

  useLayoutEffect(() => {
    if (name.includes('endTime')) {
      const localTime = getLocalTime(rangeItem);
      setValueInput(localTime);
    }
  }, [rangeItem]);

  return (
    <View
      style={[
        datePickerStyle.timeContainer,
        { width: isStartDate || name.includes('endTime') ? '48%' : '100%' },
      ]}
    >
      <>
        {props.label && (
          <View style={[datePickerStyle.labelContainer]}>
            <Text style={datePickerStyle.label}>{props.label}</Text>
          </View>
        )}
        <input
          type="time"
          id={name}
          name={name}
          style={{
            border: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'textfield',
            backgroundColor: 'transparent',
            outline: 'none',
            fontSize: 16,
            padding: '0 0 0 16px',
            fontFamily:
              'Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
            fontWeight: 400,
          }}
          onChange={(e) => {
            const newTime = e.target.value;
            handleChangeTime(newTime);
          }}
          value={valueInput || ''}
        />
      </>
    </View>
  );
};